// The static theme: a palette becomes a handful of custom properties on
// the root element, and plain CSS (src/static_theme.scss) derives every
// surface from them. Nothing is compiled at boot.

const STATUS_DEFAULTS = {
  cRed: '#d31014',
  cGreen: '#0fa00f',
  cBlue: '#0095ff',
  cOrange: '#ffa500',
}

// Pleroma Dark and Light: the only two palettes
export const DEFAULT_PALETTES = {
  dark: {
    bg: '#121a24',
    fg: '#182230',
    text: '#b9b9ba',
    link: '#d8a070',
    accent: '#d8a070',
    ...STATUS_DEFAULTS,
  },
  light: {
    bg: '#f2f4f6',
    fg: '#dbe0e8',
    text: '#304055',
    link: '#f86f0f',
    accent: '#f86f0f',
    ...STATUS_DEFAULTS,
  },
}

const parseHex = (color) => {
  const match = /^#([0-9a-f]{3,8})$/i.exec(color ?? '')
  if (!match) return null
  let hex = match[1]
  if (hex.length === 3 || hex.length === 4) {
    hex = [...hex.slice(0, 3)].map((c) => c + c).join('')
  }
  if (hex.length !== 6 && hex.length !== 8) return null
  return [0, 2, 4].map((i) => Number.parseInt(hex.slice(i, i + 2), 16) / 255)
}

// WCAG relative luminance
const luminance = (color) => {
  const rgb = parseHex(color)
  if (!rgb) return null
  const [r, g, b] = rgb.map((c) =>
    c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4,
  )
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export const contrastRatio = (a, b) => {
  const [hi, lo] = [luminance(a) ?? 0, luminance(b) ?? 0].sort((x, y) => y - x)
  return (hi + 0.05) / (lo + 0.05)
}

const toHex = (rgb) =>
  '#' +
  rgb
    .map((c) =>
      Math.round(c * 255)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')

// The colour itself if it reads on the background (WCAG AA for body text),
// otherwise the same colour mixed towards black on a light background or
// white on a dark one, just far enough to read. Palettes pick links for
// looks; Pleroma Light's orange is 2.6:1 on its own background.
export const readableAgainst = (color, background, ratio = 4.5) => {
  const rgb = parseHex(color)
  if (!rgb || contrastRatio(color, background) >= ratio) return color
  const target = (luminance(background) ?? 0) > 0.18 ? 0 : 1
  for (let step = 1; step <= 20; step++) {
    const amount = step / 20
    const candidate = toHex(rgb.map((c) => c + (target - c) * amount))
    if (contrastRatio(candidate, background) >= ratio) return candidate
  }
  return target ? '#ffffff' : '#000000'
}

// Black or white, whichever reads better on the colour. The one bit of
// colour maths CSS cannot do for us everywhere yet.
export const readableOn = (color) => {
  const l = luminance(color)
  if (l === null) return '#ffffff'
  const onBlack = (l + 0.05) / 0.05
  const onWhite = 1.05 / (l + 0.05)
  return onBlack >= onWhite ? '#000000' : '#ffffff'
}

export const isDarkPalette = (palette) => (luminance(palette.bg) ?? 0) < 0.2

export const rootTokens = (palette) => {
  const dark = isDarkPalette(palette)
  return {
    '--bg': palette.bg,
    '--fg': palette.fg,
    '--text': palette.text,
    '--link': readableAgainst(palette.link, palette.bg),
    '--accent': palette.accent,
    '--cRed': palette.cRed,
    '--cGreen': palette.cGreen,
    '--cBlue': palette.cBlue,
    '--cOrange': palette.cOrange,
    '--onAccent': readableOn(palette.accent),
    '--onRed': readableOn(palette.cRed),
    '--onGreen': readableOn(palette.cGreen),
    '--onBlue': readableOn(palette.cBlue),
    '--onOrange': readableOn(palette.cOrange),
    // fields sink away from the text; the page behind the panels always
    // sinks towards black, a lot on dark palettes and a little on light ones
    '--inverse': dark ? '#000000' : '#ffffff',
    '--pageDepth': dark ? '45%' : '7%',
    // blurred media previews are bright; dim them on a dark page
    '--mediaDim': dark ? '0.6' : '1',
    'color-scheme': dark ? 'dark' : 'light',
  }
}

export const TOKEN_NAMES = Object.keys(rootTokens(DEFAULT_PALETTES.dark))

const CLASS = '-static-theme'

export const applyStaticTheme = (palette, root = document.documentElement) => {
  Object.entries(rootTokens(palette)).forEach(([name, value]) =>
    root.style.setProperty(name, value),
  )
  root.classList.add(CLASS)
}

export const clearStaticTheme = (root = document.documentElement) => {
  TOKEN_NAMES.forEach((name) => root.style.removeProperty(name))
  root.classList.remove(CLASS)
}
